import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import queryString from "query-string";

import api from "libs/api";
import website_status_data from "libs/website_status_data.json";

const ClientsContext = createContext();

const ClientsProvider = ({ children }) => {
  const { search } = useLocation();
  const navigate = useNavigate();

  const { page: _page, ...filters } = queryString.parse(search);
  const default_sort = "tasks:desc";

  const [init_loading, setInitLoading] = useState(true);

  const { isLoading: loading, data: clients, isSuccess } = useQuery({
    queryKey: ["clients", filters],
    queryFn: () =>
      api.get(
        `/admin/clients?${queryString.stringify({
          status:
            website_status_data
              ?.filter((item) =>
                filters?.status?.split(",").includes(item?.key)
              )
              .map((item) => item?.value)
              .join(",") || undefined,
          sort: filters?.sort || default_sort,
        })}`
      ),
  });

  useEffect(() => {
    if (isSuccess) {
      setInitLoading(false);
    }
  }, [isSuccess]);

  const filterClearAll = () => {
    const query = filters?.sort
      ? `?${queryString.stringify({ sort: filters.sort })}`
      : "";

    navigate(`/clients${query}`);
  };

  const applyFilters = (newFilters) => {
    const merged = { ...filters };

    Object.entries(newFilters).forEach(([name, value]) => {
      if (!value) {
        delete merged[name];
      } else {
        merged[name] = value?.key || value;
      }
    });

    navigate(`/clients?${queryString.stringify(merged)}`);
  };

  const filterHandler = (name, value, accumulate = false) => {
    let accumulated_value = value;

    if (accumulate) {
      const currentValues = filters[name] ? filters[name].split(",") : [];

      if (value) {
        if (currentValues.includes(value)) {
          const filteredValues = currentValues.filter((val) => val !== value);
          accumulated_value =
            filteredValues.length > 0 ? filteredValues.join(",") : undefined;
        } else {
          currentValues.push(value);
          accumulated_value = currentValues.join(",");
        }
      } else {
        accumulated_value =
          currentValues.length > 0 ? currentValues.join(",") : undefined;
      }
    }

    navigate(
      `/clients?${queryString.stringify({
        ...filters,
        [name]: accumulated_value || undefined,
      })}`
    );
  };

  const value = useMemo(() => {
    return {
      data: clients,
      loading,
      init_loading,
      filters,
      filterHandler,
      filterClearAll,
      applyFilters,
    };
    // eslint-disable-next-line
  }, [clients, loading, init_loading, filters, applyFilters]);

  return (
    <ClientsContext.Provider value={value}>{children}</ClientsContext.Provider>
  );
};

const useClients = () => useContext(ClientsContext);
export { ClientsContext, useClients };
export default ClientsProvider;
